import { z } from 'zod';
import { projectsProjectAccessDecisionSchema } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectReferenceSnapshotSchema } from '@crest/module-projects/contracts/project-reference';
import { assetsAssetKindSchema } from './assets-asset.contract.js';
import { assetsCurrencyCodeSchema, assetsPlacementRateSnapshotSchema, assetsTimeZoneSchema, } from './assets-rate.contract.js';
export const assetsPlacementsCapability = 'assets.placements.v1';
export const assetsRateSummaryContractVersion = 'assets.rate-summaries.v1';
export const assetsPlacementSourceSchema = z.enum(['manual', 'import', 'provider-sync']);
export const assetsPlacementAssetReferenceSchema = z.strictObject({
    assetId: z.string().uuid(),
    label: z.string().trim().min(1).max(500),
    assetKind: assetsAssetKindSchema,
    assetRevision: z.number().int().positive(),
});
export const assetsPlacementSchema = z.strictObject({
    contractVersion: z.literal(assetsPlacementsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    assetReference: assetsPlacementAssetReferenceSchema,
    projectReference: projectsProjectReferenceSnapshotSchema,
    projectAccessDecision: projectsProjectAccessDecisionSchema,
    projectAssignmentOverride: z.boolean(),
    rateSnapshot: assetsPlacementRateSnapshotSchema,
    roomLocation: z.string().trim().max(240).nullable(),
    placedAt: z.string().datetime({ offset: true }),
    pickedUpAt: z.string().datetime({ offset: true }).nullable(),
    placedByMembershipId: z.string().uuid(),
    pickedUpByMembershipId: z.string().uuid().nullable(),
    source: assetsPlacementSourceSchema,
    revision: z.number().int().positive(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((placement, context) => {
    if (placement.projectAssignmentOverride !== placement.projectAccessDecision.assignmentOverrideRequired) {
        context.addIssue({
            code: 'custom',
            path: ['projectAssignmentOverride'],
            message: 'Placement override must match the authoritative Projects decision.',
        });
    }
    if (placement.pickedUpAt !== null && placement.pickedUpByMembershipId === null) {
        context.addIssue({
            code: 'custom',
            path: ['pickedUpByMembershipId'],
            message: 'A completed pickup requires its actor membership.',
        });
    }
});
const placementReasonSchema = z.string().trim().min(10).max(500);
export const assetsPlaceAssetCommandSchema = z.strictObject({
    kind: z.literal('place'),
    commandId: z.string().uuid(),
    placementId: z.string().uuid(),
    assetId: z.string().uuid(),
    projectId: z.string().uuid(),
    placedAt: z.string().datetime({ offset: true }),
    roomLocation: z.string().trim().max(240).nullable().default(null),
    source: assetsPlacementSourceSchema.default('manual'),
    reason: placementReasonSchema,
});
export const assetsPickupAssetCommandSchema = z.strictObject({
    kind: z.literal('pickup'),
    commandId: z.string().uuid(),
    placementId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    pickedUpAt: z.string().datetime({ offset: true }),
    roomLocation: z.string().trim().max(240).nullable().optional(),
    reason: placementReasonSchema,
});
export const assetsPlacementCommandSchema = z.discriminatedUnion('kind', [
    assetsPlaceAssetCommandSchema,
    assetsPickupAssetCommandSchema,
]);
export const assetsPlacementListQuerySchema = z.strictObject({
    assetId: z.string().uuid().nullable().default(null),
    projectId: z.string().uuid().nullable().default(null),
    activeOnly: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
});
export const assetsPlacementResponseSchema = z.strictObject({
    data: assetsPlacementSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const assetsPlacementsResponseSchema = z.strictObject({
    data: z.array(assetsPlacementSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const assetsPlacementRateSummarySchema = z.strictObject({
    placementId: z.string().uuid(),
    assetReference: assetsPlacementAssetReferenceSchema,
    projectReference: projectsProjectReferenceSnapshotSchema,
    roomLocation: z.string().trim().max(240).nullable(),
    placedAt: z.string().datetime({ offset: true }),
    pickedUpAt: z.string().datetime({ offset: true }).nullable(),
    billingThrough: z.string().datetime({ offset: true }),
    localBillingDate: z.string().date(),
    timeZone: assetsTimeZoneSchema,
    currencyCode: assetsCurrencyCodeSchema,
    billablePeriods: z.number().int().positive(),
    dailyCostCents: z.number().int().nonnegative().nullable(),
    dailyChargeCents: z.number().int().nonnegative().nullable(),
    accruedCostCents: z.number().int().nonnegative().nullable(),
    accruedChargeCents: z.number().int().nonnegative().nullable(),
    accruedProfitCents: z.number().int().nullable(),
    active: z.boolean(),
});
export const assetsProjectRateSummaryQuerySchema = z.strictObject({
    asOf: z.string().datetime({ offset: true }).default(() => new Date().toISOString()),
});
export const assetsProjectRateSummaryResponseSchema = z.strictObject({
    data: z.strictObject({
        contractVersion: z.literal(assetsRateSummaryContractVersion),
        projectReference: projectsProjectReferenceSnapshotSchema,
        asOf: z.string().datetime({ offset: true }),
        currencyCode: assetsCurrencyCodeSchema.nullable(),
        activePlacementCount: z.number().int().nonnegative(),
        totalPlacementCount: z.number().int().nonnegative(),
        totalBillablePeriods: z.number().int().nonnegative(),
        accruedCostCents: z.number().int().nonnegative().nullable(),
        accruedChargeCents: z.number().int().nonnegative().nullable(),
        accruedProfitCents: z.number().int().nullable(),
        placements: z.array(assetsPlacementRateSummarySchema),
    }),
});
//# sourceMappingURL=assets-placement.contract.js.map